import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Loader2, Sparkles, ShieldCheck, AlertTriangle, XCircle, HelpCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { PageHeader, EmptyState, PageLoading, fmtDate } from "@/components/shared";
import { DatePicker } from "@/components/DatePicker";
import { PageGuideButton } from "@/components/PageGuideButton";
import api, { formatApiErrorDetail } from "@/lib/api";

export const DOC_TYPES = ["Passport", "Residence Permit", "Zusatzblatt", "Employment Contract", "Work Authorization", "BA Approval", "Recognition Document", "Other"];

export function DocumentUpload({ employeeId, employees, onDone, worker = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [type, setType] = useState("");
  const [emp, setEmp] = useState(employeeId || "");
  const [issue, setIssue] = useState("");
  const [expiry, setExpiry] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !type || (!worker && !emp)) { toast.error(worker ? t("docp.required") : t("docp.requiredEmp")); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("type", type);
    if (!worker) fd.append("employee_id", emp);
    fd.append("issue_date", issue); fd.append("expiry_date", expiry); fd.append("notes", notes);
    try {
      await api.post(worker ? "/worker/documents" : "/employer/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("docp.uploaded"));
      setOpen(false); setFile(null); setType(""); setIssue(""); setExpiry(""); setNotes(""); if (!employeeId) setEmp("");
      onDone && onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="upload-doc-btn"><Upload className="mr-1 h-4 w-4" /> {t("docp.upload")}</Button></DialogTrigger>
      <DialogContent className="max-w-md" data-testid="upload-dialog">
        <DialogHeader><DialogTitle>{t("docp.uploadTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {!worker && !employeeId && (
            <div className="space-y-2"><Label>{t("docp.employee")}</Label>
              <Select value={emp} onValueChange={setEmp}><SelectTrigger data-testid="upload-employee"><SelectValue placeholder={t("docp.selectEmployee")} /></SelectTrigger>
                <SelectContent>{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
          <div className="space-y-2"><Label>{t("docp.type")}</Label>
            <Select value={type} onValueChange={setType}><SelectTrigger data-testid="upload-type"><SelectValue placeholder={t("docp.docType")} /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-2"><Label>{t("docp.file")}</Label><Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file" /><p className="text-xs text-slate-400">{t("docp.fileHint")}</p></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>{t("docp.issueDate")}</Label><DatePicker value={issue} onChange={setIssue} testid="upload-issue-date" /></div>
            <div className="space-y-2"><Label>{t("docp.expiryDate")}</Label><DatePicker value={expiry} onChange={setExpiry} testid="upload-expiry-date" /></div>
          </div>
          <div className="space-y-2"><Label>{t("docp.notes")}</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter><Button disabled={busy} onClick={submit} className="rounded-full bg-navy text-white" data-testid="upload-submit">{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("docp.uploading")}</> : t("docp.submit")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const AI_STYLES = {
  VERIFIED: { cls: "bg-emerald-50 text-emerald-700", Icon: ShieldCheck },
  EXPIRING: { cls: "bg-amber-50 text-amber-700", Icon: AlertTriangle },
  MISMATCH: { cls: "bg-orange-50 text-orange-700", Icon: AlertTriangle },
  EXPIRED: { cls: "bg-red-50 text-red-700", Icon: XCircle },
  UNREADABLE: { cls: "bg-slate-100 text-slate-600", Icon: HelpCircle },
  UNVERIFIED: { cls: "bg-slate-100 text-slate-600", Icon: HelpCircle },
  ERROR: { cls: "bg-slate-100 text-slate-600", Icon: HelpCircle },
  PENDING: { cls: "bg-cyan/10 text-cyan-dark", Icon: Loader2 },
};

function AiBadge({ status, t }) {
  if (!status) return null;
  const s = AI_STYLES[status] || AI_STYLES.UNVERIFIED;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.cls}`} data-testid="ai-status-badge">
      <Icon className={`h-3 w-3 ${status === "PENDING" ? "animate-spin" : ""}`} /> {t(`docai.status.${status}`)}
    </span>
  );
}

const DOC_STATUS = {
  valid: "bg-emerald-50 text-emerald-700",
  expiring: "bg-amber-50 text-amber-700",
  expired: "bg-red-50 text-red-700",
  none: "bg-slate-100 text-slate-500",
};
export function docStatus(d) {
  if (!d.expiry_date) return "none";
  const days = Math.ceil((new Date(d.expiry_date) - new Date()) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "valid";
}
function DocStatusBadge({ d, t }) {
  const s = docStatus(d);
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${DOC_STATUS[s]}`} data-testid={`doc-status-${d.id}`}>{t(`dst.${s}`)}</span>;
}

const SUMMARY_TILES = [
  { key: "valid", dot: "bg-emerald-500", active: "border-emerald-300 bg-emerald-50" },
  { key: "expiring", dot: "bg-amber-500", active: "border-amber-300 bg-amber-50" },
  { key: "expired", dot: "bg-red-500", active: "border-red-300 bg-red-50" },
  { key: "none", dot: "bg-slate-400", active: "border-slate-300 bg-slate-100" },
];
export function DocStatusSummary({ docs, active = "all", onSelect }) {
  const { t } = useTranslation();
  const counts = { valid: 0, expiring: 0, expired: 0, none: 0 };
  (docs || []).forEach((d) => { counts[docStatus(d)]++; });
  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="doc-status-summary">
      {SUMMARY_TILES.map(({ key, dot, active: activeCls }) => (
        <button key={key} onClick={() => onSelect && onSelect(active === key ? "all" : key)}
          data-testid={`doc-summary-${key}`}
          className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${active === key ? activeCls : "border-slate-100 bg-white hover:bg-slate-50"}`}>
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${dot}`} />
          <div className="min-w-0">
            <div className="text-lg font-bold leading-none text-navy" data-testid={`doc-summary-count-${key}`}>{counts[key]}</div>
            <div className="truncate text-[11px] text-slate-400">{t(`dst.${key}`)}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function DocRow({ d, onChange, showEmployee, canDelete }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const v = d.ai_verification;
  const pending = busy || v?.status === "PENDING";

  useEffect(() => {
    if (v?.status === "PENDING") {
      const timer = setTimeout(() => onChange && onChange(), 3500);
      return () => clearTimeout(timer);
    }
  }, [v?.status, d.id]);

  const download = async () => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error(t("docp.openErr")); }
  };
  const remove = async () => {
    try { await api.delete(`/employer/documents/${d.id}`); toast.success(t("docp.removed")); onChange && onChange(); }
    catch { toast.error(t("docp.removeErr")); }
  };
  const verify = async () => {
    setBusy(true);
    try { await api.post(`/documents/${d.id}/verify`); onChange && onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const applyDate = async (target) => {
    try { await api.post(`/documents/${d.id}/apply-ai-date`, { target }); toast.success(t("docai.applied")); onChange && onChange(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const ex = v?.extracted || {};
  const done = v && !["PENDING", "ERROR"].includes(v.status);

  return (
    <div className="px-4 py-3" data-testid={`doc-row-${d.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><FileText className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-navy">{d.filename}</div>
          <div className="text-xs text-slate-400">{d.type}{showEmployee && d.employee_name ? ` · ${d.employee_name}` : ""}{d.expiry_date ? ` · ${t("docp.expires")} ${fmtDate(d.expiry_date)}` : ""}</div>
        </div>
        <DocStatusBadge d={d} t={t} />
        <button onClick={download} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-navy" data-testid={`download-doc-${d.id}`}><Download className="h-4 w-4" /></button>
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild><button className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-doc-${d.id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>{t("docp.removeTitle")}</AlertDialogTitle><AlertDialogDescription>{t("docp.removeDesc", { name: d.filename })}</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>{t("docp.cancel")}</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">{t("docp.remove")}</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="mt-2 rounded-xl bg-slate-50/70 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-cyan-dark" />
            <span className="text-xs font-semibold text-slate-500">{t("docai.title")}</span>
            <AiBadge status={v?.status} t={t} />
          </div>
          <Button size="sm" variant="ghost" onClick={verify} disabled={pending} data-testid={`verify-doc-${d.id}`}
            className="h-7 rounded-full px-3 text-xs text-cyan-dark hover:bg-cyan/10">
            {pending ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" />{t("docai.rechecking")}</> : (v ? t("docai.recheck") : t("docai.verify"))}
          </Button>
        </div>

        {done && (
          <div className="mt-2 space-y-1.5">
            {v.warnings && v.warnings.length > 0 ? (
              v.warnings.map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600" data-testid={`ai-warning-${w.code}`}>
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                  <span>{t(`docai.w.${w.code}`, w.params || {})}</span>
                  {(w.code === "EXPIRY_MISMATCH" || w.code === "DOC_DATE_MISMATCH") && (
                    <button onClick={() => applyDate(w.code === "EXPIRY_MISMATCH" ? "employee" : "document")} data-testid={`apply-date-${w.code}`}
                      className="ml-1 shrink-0 rounded-full border border-cyan-300 px-2 py-0.5 text-[11px] font-semibold text-cyan-dark hover:bg-cyan/10">{t("docai.applyDate")}</button>
                  )}
                </div>
              ))
            ) : (
              <div className="flex items-center gap-1.5 text-xs text-emerald-600"><ShieldCheck className="h-3 w-3" /> {t("docai.noWarnings")}</div>
            )}
            {v.warnings.some((w) => w.code === "EXPIRY_MISMATCH") && v.warnings.some((w) => w.code === "DOC_DATE_MISMATCH") && (
              <button onClick={() => applyDate("both")} data-testid="apply-date-both" className="mt-1 rounded-full border border-cyan-400 bg-cyan/5 px-2.5 py-0.5 text-[11px] font-semibold text-cyan-dark hover:bg-cyan/10">{t("docai.applyBoth")}</button>
            )}
            {(ex.expiry_date || ex.document_number || ex.full_name) && (
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-slate-400">
                {ex.expiry_date && <span>{t("docai.expiry")}: <span className="font-medium text-slate-500">{ex.expiry_date}</span></span>}
                {ex.document_number && <span>{t("docai.docNumber")}: <span className="font-medium text-slate-500">{ex.document_number}</span></span>}
                {ex.full_name && <span>{t("docai.name")}: <span className="font-medium text-slate-500">{ex.full_name}</span></span>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function DocumentList({ docs, onChange, showEmployee = true, canDelete = true }) {
  const { t } = useTranslation();
  if (docs.length === 0) return <EmptyState icon={FileText} title={t("docp.emptyTitle")} subtitle={t("docp.emptySub")} />;
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="documents-list">
      {docs.map((d) => <DocRow key={d.id} d={d} onChange={onChange} showEmployee={showEmployee} canDelete={canDelete} />)}
    </div>
  );
}

export default function Documents() {
  const { t } = useTranslation();
  const [docs, setDocs] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("recent");
  const load = () => api.get("/employer/documents").then((r) => setDocs(r.data.documents));
  useEffect(() => { load(); api.get("/employer/employees").then((r) => setEmployees(r.data.employees)); }, []);

  let filtered = (docs || []).filter((d) => status === "all" || docStatus(d) === status);
  if (query) {
    const ql = query.toLowerCase();
    filtered = filtered.filter((d) => (d.filename || "").toLowerCase().includes(ql) || (d.type || "").toLowerCase().includes(ql) || (d.employee_name || "").toLowerCase().includes(ql));
  }
  const SORTERS = {
    recent: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    expiry: (a, b) => (a.expiry_date || "9999").localeCompare(b.expiry_date || "9999"),
    name: (a, b) => (a.filename || "").localeCompare(b.filename || ""),
  };
  filtered = [...filtered].sort(SORTERS[sort]);

  return (
    <div data-testid="documents-page">
      <PageHeader title={t("docp.title")} subtitle={t("docp.subtitle")}
        action={<div className="flex flex-wrap items-center gap-2"><PageGuideButton pageKey="documents" basePath="/employer" /><DocumentUpload employees={employees} onDone={load} /></div>} />
      {docs && docs.length > 0 && <DocStatusSummary docs={docs} active={status} onSelect={setStatus} />}
      {docs && docs.length > 0 && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center" data-testid="doc-toolbar">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("dst.search")} className="h-9 pl-9 text-sm" data-testid="doc-search-input" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-full text-xs sm:w-44" data-testid="doc-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dst.all")}</SelectItem>
              <SelectItem value="valid">{t("dst.valid")}</SelectItem>
              <SelectItem value="expiring">{t("dst.expiring")}</SelectItem>
              <SelectItem value="expired">{t("dst.expired")}</SelectItem>
              <SelectItem value="none">{t("dst.none")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-full text-xs sm:w-44" data-testid="doc-sort-select"><SelectValue placeholder={t("dst.sort")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">{t("dst.sortRecent")}</SelectItem>
              <SelectItem value="oldest">{t("dst.sortOldest")}</SelectItem>
              <SelectItem value="expiry">{t("dst.sortExpiry")}</SelectItem>
              <SelectItem value="name">{t("dst.sortName")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {docs === null ? <PageLoading /> : <DocumentList docs={filtered} onChange={load} />}
    </div>
  );
}
